import { Form, FormGroup } from "@angular/forms";
import type { VehicleField, FieldValidationRule } from "@/app/pages/fleet-management/vehicle-form-data";
import type { LanguageService } from "@/app/core/services/language.service";

export function generateGUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}


export function splitOEMPRU(oemPru: string): [string, string]{
  const parts = oemPru.split("-").map(item => item.trim());
  if (parts.length !== 2) {
    throw new Error("Input must be in 'OEM - PRU' format");
  }
  return [parts[0], parts[1]]; // 明确返回元组类型
}

export function unslugify(slug: string) {
  const words = slug.replace(/-/g, ' ').split(' ');
  return words.join(' ');
}

export async function calculateFileHash(file: File, algorithm = 'SHA-256'): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest(algorithm, buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function clearMSALInteractionState() {
  // Iterate over all keys in sessionStorage (snapshot keys to avoid live-collection issues)
  const keys = Array.from(Object.keys(sessionStorage));
  keys.forEach(key => {
    // Remove all keys related to MSAL interaction state, typically those containing 'msal.' prefix and 'interaction' substring
    if (key.includes('msal.') && key.includes('interaction')) {
      sessionStorage.removeItem(key);
    }
  });
}

export function isFormControlValid(controlName: string, form: FormGroup<any>): boolean {
  const formControl = form.get(controlName);
  if (!formControl) return false;

  if(formControl.dirty || formControl.touched) {
    return formControl.valid;
  }
  return true;
}

export function getFormState(controlName: string, form: FormGroup<any>) {
  return isFormControlValid(controlName, form) ? '': 'error';
}

export function getFormItemHelperText(
  controlName: string,
  form: FormGroup<any>,
  field?: any
): string {
  if(isFormControlValid(controlName, form)) {
    return field?.helper ?? '';
  }
  return getFormErrorMessages(controlName, form, field);
}

export function getTranslatedFormItemHelperText(
  controlName: string,
  form: FormGroup<any>,
  field?: any,
  languageService?: LanguageService
): string {
  if(isFormControlValid(controlName, form)) {
    // When control is valid, try to get translated helper text
    if (languageService && (field as any)?.helperKey) {
      languageService.translationTrigger();
      return languageService.instant((field as any).helperKey);
    }
    return field?.helper ?? '';
  }
  return getTranslatedFormErrorMessages(controlName, form, field, languageService);
}

export function getTranslatedFormErrorMessages(controlName: string, form: FormGroup<any>, field?: any, languageService?: LanguageService): string {
  const errors = form.get(controlName)?.errors;
  const label = (languageService && field?.labelKey) ? languageService.instant(field.labelKey) : (field?.label ?? controlName);
  if (errors) {
    if (errors['serverError']) {
      return errors['serverError'];
    }
    if (errors['required']) {
      return languageService ? languageService.instant('form.error.required', { label }) : `${label} is required!`;
    }
    if (errors['pattern']) {
      const msg = getValidationRuleMessage(field, 'pattern');
      return msg ?? (languageService ? languageService.instant('form.error.pattern', { label }) : `${label} has an invalid format.`);
    }
    if (errors['min']) {
      return languageService ? languageService.instant('form.error.min', { label, min: errors['min'].min }) : `${label} must be at least ${errors['min'].min}.`;
    }
    if (errors['invalidNodeID']) {
      return languageService ? languageService.instant('form.error.invalidNodeID') : 'Only decimal or hexadecimal are supported';
    }
  }
  return '';
}

function getFormErrorMessages(controlName: string, form: FormGroup<any>, field?: any): string {
  const errors = form.get(controlName)?.errors;
  const label = field?.label ?? controlName;
  if (errors) {
    if (errors['required']) {
      return `${label} is required!`;
    }
    if (errors['pattern']) {
      return getValidationRuleMessage(field, 'pattern') ?? `${label} has an invalid format.`;
    }
    if (errors['min']) {
      return getValidationRuleMessage(field, 'min') ?? `${label} must be at least ${errors['min'].min}.`;
    }
    if (errors['invalidNodeID']) {
      return `Only decimal or hexadecimal are supported`;
    }
  }
  return field?.helper ?? '';
}

function getValidationRuleMessage(field?: any, type?: FieldValidationRule['type']): string | undefined {
  return field?.validationRules?.find((rule: { type: string | undefined; }) => rule?.type === type)?.message;
}
